// Pure stat + hit resolution. The single place combat/skill numbers are computed, so Phase 8
// gear is a DATA change (swap the scalar source) not a rewrite. (TECHNICAL_DESIGN.md §8.)
import type { Rng } from './rng';
import type { Stats } from './types';

export const FISTS_ATK = 3;

// Baseline crafted gear → scalar stats. At Phase 8, equipped GearItem.base replaces the tier
// integer as the source; computeStats never changes.
export function baselineWeaponAtk(kind: 'melee' | 'bow', tier: number): number {
  if (kind === 'bow') return [0, 6, 12][tier] ?? 0; // 0 = no bow; wood 6 / iron 12
  return [FISTS_ATK, 8, 16][tier] ?? FISTS_ATK; // fists / stone / iron
}
export function baselineArmorDef(tier: number): number {
  return [0, 4, 10][tier] ?? 0; // none / leather / iron
}

// Skill XP → level (quadratic curve: level L reached at 25·L² XP).
export function xpToLevel(xp: number): number {
  return Math.floor(Math.sqrt(Math.max(0, xp) / 25));
}
export function xpForLevel(level: number): number {
  return 25 * level * level;
}
export function combatMult(combatXp: number): number {
  return 1 + xpToLevel(combatXp) * 0.02; // +2% attack per combat level
}
/** Gather power multiplier from a use-skill (+3% per level). */
export function gatherMult(skillXp: number): number {
  return 1 + xpToLevel(skillXp) * 0.03;
}

export interface StatInput {
  weaponAtk: number; // game derives from equipped weapon or the crafted tier baseline
  weaponDef: number; // from equipped armor or the crafted armor tier
  foodMaxHp: number;
  foodSpdPct: number;
  warmedSpdPct: number;
  combatXp: number;
  critBonus?: number; // from gear crit affixes
}

export function computeStats(inp: StatInput): Stats {
  return {
    atk: inp.weaponAtk * combatMult(inp.combatXp),
    maxHp: 100 + inp.foodMaxHp,
    def: inp.weaponDef,
    spdPct: inp.foodSpdPct + inp.warmedSpdPct,
    critPct: 5 + (inp.critBonus ?? 0),
  };
}

/** Pure hit roll. Crit uses an INJECTED rng stream (not world-seed) — anti save-scum.
 *  Armor `def` on the target subtracts flat, min 1. */
export function computeHit(atk: number, critPct: number, rng: Rng, targetDef = 0): { dmg: number; crit: boolean } {
  const crit = rng.float() * 100 < critPct;
  return { dmg: Math.max(1, Math.round(atk * (crit ? 2 : 1)) - targetDef), crit };
}
